import { ReportService } from "../../../services/ReportsService";
import { useEffect, useState, useMemo } from "react";
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import { useTranslation } from "react-i18next";
import { AlertService } from "../../../services/Alert";
import useAccounts from '../../../hooks/useAccounts';
import Aside from "../../../components/Aside";
import Footer from "../../../components/Footer";
import Header from "../../../components/Header";
import CustomDataTable from "../../../components/CustomDataTable";
import DatePicker from "../../../components/form-controls/DatePicker";
import Select from 'react-select'
import useRequest from "../../../hooks/useRequest";


function ApiPage() {

    const { t } = useTranslation();
    const { account } = useCurrentAccount();
    const { accounts } = useAccounts();
    const { call, isRequesting } = useRequest();
    const [data, setData] = useState([])
    const [accountID, setAccountID] = useState(account.id)
    const [filterRangeDates, setFilterRangeDates] = useState({ start: null, end: null })
    const [selectedAccount, setSelectedAccount] = useState(null);


    const columns = [
        {
            name: t('number'),
            sortable: false,
            center: false,
            selector: (row: { number: string; }) => row.number,
            cell: (row: any) => {
                return (
                    <div className="text-left">
                        <p className="mb-1">
                            <span className="fs-4 fw-bolder h4">{row.number}</span>
                        </p>
                    </div>
                )
            }
        },
        {
            name: 'Status',
            sortable: false,
            width: 20,
            center: true,
            selector: (row: { status: string; }) => row.status,
            cell: (row: any) => {
                let badgeContent;

                switch (row.status) {
                    case 'leído':
                        badgeContent = (
                            <span className="badge bg-soft-info text-info">
                                <i className="bi bi-check-all fs-2 mt-10"></i>
                                <span className="fs-5 ms-1 mb-auto">{t('reports.API.s3')}</span>
                            </span>
                        );
                        break;
                    case 'entregado':
                        badgeContent = (
                            <span className="badge bg-soft-secondary text-secondary">
                                <i className="bi bi-check-circle-fill fs-5"></i>
                                <span className="fs-6 ms-1">{t('reports.API.s2')}</span>
                            </span>
                        );
                        break;
                    case 'fallido':
                        badgeContent = (
                            <span className="badge bg-soft-danger text-danger">
                                <i className="bi bi-x-circle-fill fs-5"></i>
                                <span className="fs-6 ms-1">{t('reports.API.s4')}</span>
                            </span>
                        );
                        break;
                    case 'enviado':
                        badgeContent = (
                            <span className="badge bg-soft-success text-success">
                                <i className="bi bi-send-fill fs-5"></i>
                                <span className="fs-6 ms-1">{t('reports.API.s1')}</span>
                            </span>
                        );
                        break;
                    default:
                        badgeContent = (
                            <span className="badge bg-soft-success text-success">
                                <i className="bi bi-dash-circle-fill fs-6"></i>
                                <span className="fs-6 ms-1">{row.status}</span>
                            </span>
                        );
                        break;
                }

                return <p className="text-nowrap mb-0">{badgeContent}</p>;
            }
        },
        {
            name: t('reports.API.c3'),
            sortable: false,
            center: false,
            selector: (row: { message: string; }) => row.message,
            cell: (row: any) => {
                return (
                    <div className="text-left">
                        <p className="mb-1 text-wrap">
                            <span className="fs-4">{row.message}</span>
                        </p>
                    </div>
                );
            }
        },
        {
            name: t('reports.API.c4'),
            sortable: false,
            center: false,
            selector: (row: { date_send: string; }) => row.date_send,
            cell: (row: any) => {

                const [date, hour] = row.date_send.split(' ');

                return (
                    <div className="text-left">
                        <p className="mb-1 text-nowrap">
                            <span className="h4">{t('date')}: </span> <span className="fs-4">{date}</span>
                        </p>
                        <p className="mb-1 text-nowrap">
                            <span className="h4">{t('hour')}: </span> <span className="fs-4">{hour}</span>
                        </p>
                    </div>
                )
            }
        },
        {
            name: t('reports.API.c5'),
            sortable: false,
            center: false,
            selector: (row: { last_update: string; }) => row.last_update,
            cell: (row: any) => {

                const [date, hour] = row.last_update.split(' ');

                return (
                    <div className="text-left">
                        <p className="mb-1 text-nowrap">
                            <span className="h4">{t('date')}: </span> <span className="fs-4">{date}</span>
                        </p>
                        <p className="mb-1 text-nowrap">
                            <span className="h4">{t('hour')}: </span> <span className="fs-4">{hour}</span>
                        </p>
                    </div>
                )
            }
        },
    ];


    const getAPI = async (accountId: number) => {
        if (!filterRangeDates.start || !filterRangeDates.end) return;

        const init_date = filterRangeDates.start.format('YYYY-MM-DD');
        const end_date = filterRangeDates.end.format('YYYY-MM-DD');

        try {
            const { data } = await call(() => ReportService.getApi(accountId, init_date, end_date));
            setData(data);
        } catch (error) {
            AlertService.toastError(t('reports.API.error-1'))
            console.error(t('reports.API.error-2'), error);
        }
    };

    const handleAccountChange = (selectedOption: any) => {
        const accountId = selectedOption ? selectedOption.value : account.id;
        setAccountID(accountId)
        setSelectedAccount(selectedOption);
        getAPI(accountId);
    };


    useEffect(() => {
        getAPI(accountID);
    }, [filterRangeDates])

    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("reports.operation-title")}</span></li>
                                        <li className="breadcrumb-item " aria-current="page"><span className="breadcrumb-link" >{t("reports.reports-title")}</span></li>
                                        <li className="breadcrumb-item active" aria-current="page">API</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title"><i className="bi-grid-1x2 nav-icon"></i> {t("reports.reports-title")} - API</h1>
                            </div>
                            <div className="col-sm-auto">
                                <Select
                                    options={accounts.map(a => ({ value: a.account_id, label: a.nombre }))}
                                    className={'form-select-custom width-select'}
                                    classNamePrefix='form-select-custom'
                                    value={selectedAccount}
                                    onChange={handleAccountChange}
                                    placeholder={t("reports.billing.select-placeholder")}
                                    isClearable
                                />
                            </div>
                            <div className="col-sm-auto">

                                <DatePicker onDatesChange={setFilterRangeDates} />
                            </div>
                        </div>
                    </div>
                    <div className='row mt-3 p-2'>
                        <CustomDataTable
                            columns={columns}
                            data={data}
                            hideAccountsFilter
                            showExport={true}
                            isLoading={isRequesting}
                            onDatesChange={(range) => setFilterRangeDates(range)}
                            filter={(row, term) => {
                                if (!term) return true;
                                return (
                                    `${row.number}`.match(new RegExp(`${term}`, 'i')) ||
                                    `${row.status}`.match(new RegExp(`${term}`, 'i')) ||
                                    `${row.message}`.match(new RegExp(`${term}`, 'i')) ||
                                    `${row.date_send}`.match(new RegExp(`${term}`, 'i')) ||
                                    `${row.last_update}`.match(new RegExp(`${term}`, 'i'))
                                );
                            }}
                        />
                    </div>
                </div>
                <Footer />
            </main>
        </>
    )
}
export default ApiPage